import React, { useState, useEffect, useRef } from 'react';
import { Table, Empty, Button, Modal, Form, Input, Space, Tooltip, Popconfirm, message, Avatar, Upload } from 'antd';
import {
  CreditCardOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PictureOutlined,
  DeleteFilled,
  UploadOutlined,
} from '@ant-design/icons';
import { financeSettingsApi, show422Error, ValidationError } from '../../utils/api';
import { TableSkeleton } from '../SkeletonLoaders';

const { TextArea } = Input;

const InvoicePaymentTab = ({ data, onUpdate }) => {
  const [form] = Form.useForm();
  const [payments, setPayments] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [imageDeleted, setImageDeleted] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (data?.payment) {
      setPayments(data.payment);
    }
  }, [data]);

  const handleAdd = () => {
    setEditingPayment(null);
    setCurrentImageUrl(null);
    setImageDeleted(false);
    setSelectedFile(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = async (record) => {
    setDetailLoading(true);
    setEditingPayment(record);
    setImageDeleted(false);
    setModalVisible(true);
    try {
      const res = await financeSettingsApi.getPaymentDetail(record.id);
      const payment = res?.data?.payment || res?.payment || record;
      form.setFieldsValue({
        title: payment.title,
        payment_details: payment.payment_details,
      });
      setCurrentImageUrl(payment.image_url && payment.image_url !== '-' ? payment.image_url : null);
    } catch (error) {
      console.error('Error fetching payment detail:', error);
      form.setFieldsValue({
        title: record.title,
        payment_details: record.payment_details,
      });
      setCurrentImageUrl(record.image_url && record.image_url !== '-' ? record.image_url : null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await financeSettingsApi.deletePaymentDetail(id);
      message.success('روش پرداخت با موفقیت حذف شد');
      onUpdate?.();
    } catch (error) {
      console.error('Error deleting payment detail:', error);
      if (error instanceof ValidationError) {
        show422Error(error, form);
      } else {
        message.error('خطا در حذف روش پرداخت');
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        title: values.title,
        payment_details: values.payment_details || '',
      };

      if (selectedFile) {
        payload.image = selectedFile;
      }

      if (editingPayment) {
        if (imageDeleted) {
          payload.image_delete = 'yes';
        }
        await financeSettingsApi.updatePaymentDetail(editingPayment.id, payload);
        message.success('روش پرداخت با موفقیت ویرایش شد');
      } else {
        await financeSettingsApi.createPaymentDetail(payload);
        message.success('روش پرداخت جدید با موفقیت ایجاد شد');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingPayment(null);
      setCurrentImageUrl(null);
      setImageDeleted(false);
      setSelectedFile(null);
      onUpdate?.();
    } catch (error) {
      if (error?.errorFields) return;
      console.error('Error saving payment detail:', error);
      if (error instanceof ValidationError) {
        show422Error(error, form);
      } else {
        message.error('خطا در ذخیره روش پرداخت');
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'تصویر',
      dataIndex: 'image_url',
      key: 'image',
      width: 70,
      align: 'center',
      render: (url) =>
        url && url !== '-' ? (
          <Avatar src={url} shape="square" size={36} />
        ) : (
          <Avatar icon={<PictureOutlined />} shape="square" size={36} style={{ backgroundColor: '#f0f0f0', color: '#bfbfbf' }} />
        ),
    },
    {
      title: 'عنوان',
      dataIndex: 'title',
      key: 'title',
      render: (text) => (
        <div className="flex items-center gap-2">
          <CreditCardOutlined className="text-blue-500" />
          <span className="font-medium">{text}</span>
        </div>
      ),
    },
    {
      title: 'جزئیات پرداخت',
      dataIndex: 'payment_details',
      key: 'payment_details',
      ellipsis: true,
      render: (text) => <span className="text-gray-500">{text || '-'}</span>,
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 140,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Tooltip title="ویرایش">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              className="text-blue-500 hover:text-blue-600"
            />
          </Tooltip>
          <Popconfirm
            title="حذف روش پرداخت"
            description="آیا از حذف این روش پرداخت اطمینان دارید؟"
            onConfirm={() => handleDelete(record.id)}
            okText="بله، حذف کن"
            cancelText="خیر"
            okButtonProps={{ danger: true, style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } }}
            cancelButtonProps={{ style: { borderColor: '#d9d9d9', color: '#595959' } }}
          >
            <Tooltip title="حذف">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                className="text-red-500 hover:text-red-600"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      <div className="flex items-center justify-between mb-4">
        <div />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          className="bg-green-600 hover:bg-green-700 border-0"
          style={{ fontFamily: 'CustomFont' }}
        >
          افزودن روش پرداخت جدید
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={payments}
        rowKey="id"
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              description={<span style={{ fontFamily: 'CustomFont' }}>هیچ روش پرداختی ثبت نشده</span>}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        style={{ fontFamily: 'CustomFont' }}
      />

      <Modal
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingPayment(null);
          setCurrentImageUrl(null);
          setImageDeleted(false);
          setSelectedFile(null);
          form.resetFields();
        }}
        title={
          <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
            <CreditCardOutlined className="text-blue-500" />
            <span>{editingPayment ? 'ویرایش روش پرداخت' : 'افزودن روش پرداخت جدید'}</span>
          </div>
        }
        footer={null}
        centered
        width={500}
        destroyOnClose
      >
        {detailLoading ? (
          <TableSkeleton rows={3} columns={1} />
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ fontFamily: 'CustomFont' }}
          >
            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>عنوان</span>}
              name="title"
              rules={[{ required: true, message: 'عنوان الزامی است' }]}
            >
              <Input placeholder="مثلا: کارت به کارت" style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>جزئیات پرداخت</span>}
              name="payment_details"
            >
              <TextArea rows={3} placeholder="توضیحات روش پرداخت..." style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>

            {editingPayment && currentImageUrl && !imageDeleted && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontFamily: 'CustomFont' }}>
                  تصویر فعلی
                </label>
                <div className="flex items-center gap-3">
                  <Avatar src={currentImageUrl} shape="square" size={64} />
                  <Button
                    danger
                    size="small"
                    icon={<DeleteFilled />}
                    onClick={() => setImageDeleted(true)}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    حذف تصویر
                  </Button>
                </div>
              </div>
            )}

            {editingPayment && imageDeleted && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <span className="text-red-600 text-sm" style={{ fontFamily: 'CustomFont' }}>
                  تصویر پس از ذخیره حذف خواهد شد.
                </span>
                <Button
                  type="link"
                  size="small"
                  onClick={() => setImageDeleted(false)}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  انصراف
                </Button>
              </div>
            )}

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>{editingPayment ? 'تغییر تصویر' : 'تصویر'}</span>}
            >
              <Upload
                beforeUpload={(file) => {
                  const isImage = file.type.startsWith('image/');
                  if (!isImage) {
                    message.error('فقط فایل‌های تصویری مجاز هستند');
                    return Upload.LIST_IGNORE;
                  }
                  const isLt5M = file.size / 1024 / 1024 < 5;
                  if (!isLt5M) {
                    message.error('حجم تصویر باید کمتر از ۵ مگابایت باشد');
                    return Upload.LIST_IGNORE;
                  }
                  setSelectedFile(file);
                  return false;
                }}
                onRemove={() => setSelectedFile(null)}
                fileList={selectedFile ? [{ uid: '-1', name: selectedFile.name, status: 'done' }] : []}
                maxCount={1}
                accept="image/*"
              >
                <Button icon={<UploadOutlined />} style={{ fontFamily: 'CustomFont' }}>
                  انتخاب تصویر
                </Button>
              </Upload>
            </Form.Item>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setEditingPayment(null);
                  setCurrentImageUrl(null);
                  setImageDeleted(false);
                  setSelectedFile(null);
                  form.resetFields();
                }}
                style={{ fontFamily: 'CustomFont', borderColor: '#d9d9d9', color: '#595959' }}
              >
                انصراف
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="bg-green-600 hover:bg-green-700 border-0"
                style={{ fontFamily: 'CustomFont' }}
              >
                {editingPayment ? 'ذخیره تغییرات' : 'ایجاد'}
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default InvoicePaymentTab;
